import { useRef, useState } from "react";

import { TOPIC_OPTIONS } from "pages/chat/common/topic-options";
import { useChatContext } from "pages/chat/context/chat";
import {
  MobileFilterInfoPanel,
  SidebarFilterChip,
  SidebarFilterChipInfo,
  SidebarFilterChipSymbol,
  SidebarFilterHeader,
  SidebarFilterRow,
  SidebarFilterTitle,
  SidebarFilterTooltip,
  SidebarFilterWrap,
} from "./styles";

export default function SidebarTopicFilters() {
  const chatCtx = useChatContext();
  const selectedTopics = chatCtx.selectedTopics;
  const isShowingAllTopics = selectedTopics.length === 0;
  const [mobileInfo, setMobileInfo] = useState<{
    arrowLeft: number;
    left: number;
    text: string;
    width: number;
  } | null>(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 900;
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const openMobileInfo = (description: string, chipElement: HTMLElement) => {
    const wrapElement = wrapRef.current;
    if (!wrapElement || typeof window === "undefined" || window.innerWidth > 900) {
      return;
    }

    const wrapRect = wrapElement.getBoundingClientRect();
    const chipRect = chipElement.getBoundingClientRect();
    const maxWidth = Math.min(240, Math.max(160, wrapRect.width - 20));
    const chipCenter = chipRect.left - wrapRect.left + chipRect.width / 2;
    const idealLeft = chipCenter - maxWidth / 2;
    const clampedLeft = Math.max(10, Math.min(idealLeft, wrapRect.width - maxWidth - 10));
    const arrowLeft = Math.max(16, Math.min(chipCenter - clampedLeft - 6, maxWidth - 28));

    setMobileInfo((currentInfo) =>
      currentInfo?.text === description
        ? null
        : { arrowLeft, left: clampedLeft, text: description, width: maxWidth }
    );
  };

  const handleClearAll = () => {
    setMobileInfo(null);
    chatCtx.onClearTopicFilter();
  };

  const handleToggleTopic = (topic: (typeof TOPIC_OPTIONS)[number]["value"]) => {
    setMobileInfo(null);
    chatCtx.onToggleTopic(topic);
  };

  return (
    <SidebarFilterWrap ref={wrapRef}>
      <SidebarFilterHeader>
        <SidebarFilterTitle>אפשר לסנן את השיחות לפי נושא</SidebarFilterTitle>
      </SidebarFilterHeader>
      <SidebarFilterRow>
        <SidebarFilterChip
          role="button"
          tabIndex={0}
          aria-pressed={isShowingAllTopics}
          $isSelected={isShowingAllTopics}
          onClick={handleClearAll}
          onTouchEnd={(event) => {
            event.preventDefault();
            handleClearAll();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleClearAll();
            }
          }}
        >
          <span>הכל</span>
        </SidebarFilterChip>
        {TOPIC_OPTIONS.map((topic) => {
          const isSelected = selectedTopics.includes(topic.value);

          return (
            <SidebarFilterChip
              key={topic.value}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              $isSelected={isSelected}
              onClick={() => handleToggleTopic(topic.value)}
              onTouchEnd={(event) => {
                event.preventDefault();
                handleToggleTopic(topic.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleToggleTopic(topic.value);
                }
              }}
            >
              <SidebarFilterChipSymbol aria-hidden="true">
                {topic.symbol}
              </SidebarFilterChipSymbol>
              <span>{topic.label}</span>
              <SidebarFilterChipInfo
                type="button"
                aria-label={`מידע על ${topic.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (window.innerWidth <= 900) {
                    openMobileInfo(topic.description, event.currentTarget.closest("[role='button']") as HTMLElement);
                  }
                }}
                onTouchEnd={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (window.innerWidth <= 900) {
                    openMobileInfo(topic.description, event.currentTarget.closest("[role='button']") as HTMLElement);
                  }
                }}
              >
                i
              </SidebarFilterChipInfo>
              {!isMobile && <SidebarFilterTooltip>{topic.description}</SidebarFilterTooltip>}
            </SidebarFilterChip>
          );
        })}
      </SidebarFilterRow>
      {mobileInfo && (
        <MobileFilterInfoPanel
          $arrowLeft={mobileInfo.arrowLeft}
          $left={mobileInfo.left}
          $width={mobileInfo.width}
        >
          {mobileInfo.text}
        </MobileFilterInfoPanel>
      )}
    </SidebarFilterWrap>
  );
}
